import React, { useCallback, useMemo, useState } from 'react';
import { Star, Heart, Video, MessageSquare, FileText, Play, BookOpen, Headphones, Search, ArrowUpDown, Clock, Sparkles, User, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { useLanguage } from '../../i18n/LanguageContext';
interface FavoritesTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
type TabType = 'astrologers' | 'services' | 'products' | 'courses';
type SortType = 'recent' | 'rating' | 'price';
interface FavAstrologer {
  id: string;
  name: string;
  spec: string;
  rating: number;
  reviews: number;
  price: string;
  priceNum: number;
  initials: string;
  color: string;
  online: boolean;
  nextSlot?: string;
  addedDaysAgo: number;
  active: boolean;
}
interface FavService {
  id: string;
  title: string;
  astrologer: string;
  price: string;
  priceNum: number;
  rating: number;
  format: 'video' | 'text';
  addedDaysAgo: number;
}
interface FavProduct {
  id: string;
  title: string;
  type: string;
  price: string;
  priceNum: number;
  icon: string;
  addedDaysAgo: number;
}
interface FavCourse {
  id: string;
  title: string;
  lessons: number;
  duration: string;
  price: string;
  priceNum: number;
  addedDaysAgo: number;
}
const initialData = {
  astrologers: [{
    id: 'a1',
    name: 'Елена Смирнова',
    spec: 'Западная астрология · Натальная карта',
    rating: 4.9,
    reviews: 124,
    price: 'от 4 000 ₽',
    priceNum: 4000,
    initials: 'ЕС',
    color: 'bg-indigo-100 text-indigo-700',
    online: true,
    nextSlot: 'Сегодня, 18:00',
    addedDaysAgo: 1,
    active: true
  }, {
    id: 'a2',
    name: 'Михаил Волков',
    spec: 'Ведическая астрология · Прогностика',
    rating: 5.0,
    reviews: 86,
    price: 'от 4 500 ₽',
    priceNum: 4500,
    initials: 'МВ',
    color: 'bg-emerald-100 text-emerald-700',
    online: false,
    nextSlot: 'Завтра, 10:00',
    addedDaysAgo: 3,
    active: true
  }, {
    id: 'a3',
    name: 'Анна Коваль',
    spec: 'Синастрия · Отношения',
    rating: 4.8,
    reviews: 210,
    price: 'от 3 500 ₽',
    priceNum: 3500,
    initials: 'АК',
    color: 'bg-rose-100 text-rose-700',
    online: true,
    nextSlot: 'Сегодня, 20:30',
    addedDaysAgo: 7,
    active: true
  }, {
    id: 'a4',
    name: 'Дмитрий Орлов',
    spec: 'Хорарная астрология',
    rating: 4.6,
    reviews: 42,
    price: 'от 3 000 ₽',
    priceNum: 3000,
    initials: 'ДО',
    color: 'bg-amber-100 text-amber-700',
    online: false,
    addedDaysAgo: 14,
    active: false
  }] as FavAstrologer[],
  services: [{
    id: 's1',
    title: 'Разбор натальной карты',
    astrologer: 'Елена Смирнова',
    price: '5 000 ₽',
    priceNum: 5000,
    rating: 4.9,
    format: 'video' as const,
    addedDaysAgo: 2
  }, {
    id: 's2',
    title: 'Соляр на год',
    astrologer: 'Михаил Волков',
    price: '4 000 ₽',
    priceNum: 4000,
    rating: 5.0,
    format: 'text' as const,
    addedDaysAgo: 5
  }] as FavService[],
  products: [{
    id: 'p1',
    title: 'Астрологический планер 2025',
    type: 'PDF',
    price: '1 500 ₽',
    priceNum: 1500,
    icon: 'book',
    addedDaysAgo: 1
  }, {
    id: 'p2',
    title: 'Набор медитаций для гармонизации планет',
    type: 'Аудио',
    price: '990 ₽',
    priceNum: 990,
    icon: 'audio',
    addedDaysAgo: 10
  }] as FavProduct[],
  courses: [{
    id: 'c1',
    title: 'Основы натальной астрологии',
    lessons: 12,
    duration: '4 недели',
    price: '15 000 ₽',
    priceNum: 15000,
    addedDaysAgo: 4
  }] as FavCourse[]
};
const PAGE_SIZE = 20;
export function FavoritesTab({
  theme,
  onNavigate
}: FavoritesTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('astrologers');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortType, setSortType] = useState<SortType>('recent');
  const [favorites, setFavorites] = useState(initialData);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const totalCount = favorites.astrologers.length + favorites.services.length + favorites.products.length + favorites.courses.length;
  const tabs: {
    id: TabType;
    label: string;
    count: number;
  }[] = [{
    id: 'astrologers',
    label: t('userdash.favorites.astrologers'),
    count: favorites.astrologers.length
  }, {
    id: 'services',
    label: t('userdash.favorites.services'),
    count: favorites.services.length
  }, {
    id: 'products',
    label: t('userdash.favorites.products'),
    count: favorites.products.length
  }, {
    id: 'courses',
    label: t('userdash.favorites.courses'),
    count: favorites.courses.length
  }];
  const sortLabels: Record<SortType, string> = {
    recent: t('userdash.favorites.sortRecent'),
    rating: t('userdash.favorites.sortRating'),
    price: t('userdash.favorites.sortPrice')
  };
  const nextSort = () => {
    const order: SortType[] = ['recent', 'rating', 'price'];
    setSortType(order[(order.indexOf(sortType) + 1) % order.length]);
  };
  const handleRemove = useCallback((type: TabType, id: string) => {
    setFavorites(prev => ({
      ...prev,
      [type]: (prev[type] as any[]).filter((item: any) => item.id !== id)
    }));
    setExpandedId(null);
    showToast(t('userdash.favorites.removedFromFavorites'));
  }, [showToast, t]);
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setVisibleCount(PAGE_SIZE);
    setExpandedId(null);
  };
  const toggle = (id: string) => setExpandedId(expandedId === id ? null : id);
  const daysAgoLabel = (d: number) => {
    if (d === 0) return t('userdash.favorites.today');
    if (d === 1) return t('userdash.favorites.yesterday');
    if (d <= 6) return t('userdash.favorites.daysAgo', {
      count: d.toString()
    });
    return t('userdash.favorites.weeksAgo', {
      count: Math.floor(d / 7).toString()
    });
  };
  // Filtered lists
  const filteredAstrologers = useMemo(() => {
    let list = [...favorites.astrologers];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a => a.name.toLowerCase().includes(q) || a.spec.toLowerCase().includes(q));
    }
    if (sortType === 'rating') list.sort((a, b) => b.rating - a.rating);else if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.astrologers, searchQuery, sortType]);
  const filteredServices = useMemo(() => {
    let list = [...favorites.services];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => s.title.toLowerCase().includes(q) || s.astrologer.toLowerCase().includes(q));
    }
    if (sortType === 'rating') list.sort((a, b) => b.rating - a.rating);else if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.services, searchQuery, sortType]);
  const filteredProducts = useMemo(() => {
    let list = [...favorites.products];
    if (searchQuery.trim()) list = list.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.products, searchQuery, sortType]);
  const filteredCourses = useMemo(() => {
    let list = [...favorites.courses];
    if (searchQuery.trim()) list = list.filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()));
    if (sortType === 'price') list.sort((a, b) => a.priceNum - b.priceNum);else list.sort((a, b) => a.addedDaysAgo - b.addedDaysAgo);
    return list;
  }, [favorites.courses, searchQuery, sortType]);
  const topRecommendation = favorites.astrologers.filter(a => a.online && a.active).sort((a, b) => b.rating - a.rating)[0];
  // Paginated current list
  const getCurrentList = (): any[] => {
    if (activeTab === 'astrologers') return filteredAstrologers;
    if (activeTab === 'services') return filteredServices;
    if (activeTab === 'products') return filteredProducts;
    return filteredCourses;
  };
  const currentList = getCurrentList();
  const visibleItems = currentList.slice(0, visibleCount);
  const hasMore = visibleCount < currentList.length;
  const renderEmpty = (msg: string, btn: string, page: string) => <div className="flex flex-col items-center py-12 text-center" data-id="element-19598">
      <Heart className="w-10 h-10 text-slate-200 mb-3" data-id="element-19599" />
      <h3 className="font-bold text-slate-900 text-sm mb-1" data-id="element-19600">{msg}</h3>
      <p className="text-xs text-slate-500 mb-4 max-w-xs" data-id="element-19601">
        {t('userdash.favorites.saveToReturn')}
      </p>
      <Button size="sm" onClick={() => onNavigate(page)} data-id="element-19602">
        {btn}
      </Button>
    </div>;
  return <div className="space-y-4 max-w-3xl pb-8" data-id="element-19603">
      <div data-id="element-19604">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19605">
          {t('userdash.favorites.title')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-19606">
          {t('userdash.favorites.savedItems', {
          count: totalCount.toString()
        })}
        </p>
      </div>

      {topRecommendation && <div className="flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-xl" data-id="element-19607">
          <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" data-id="element-19608" />
          <span className="text-xs text-indigo-900 flex-1 min-w-0 truncate" data-id="element-19609">
            <span className="font-semibold" data-id="element-19610">{topRecommendation.name}</span>{' '}
            {t('userdash.favorites.online')}
            {topRecommendation.nextSlot && ` · ${topRecommendation.nextSlot}`}
          </span>
          <Button size="sm" className="text-xs shrink-0 h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-19611">
            {t('userdash.favorites.book')}
          </Button>
        </div>}

      <div className="flex gap-2" data-id="element-19612">
        <div className="relative flex-1" data-id="element-19613">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-19614" />
          <input type="text" placeholder={t('userdash.favorites.search')} value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setVisibleCount(PAGE_SIZE);
        }} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-white" data-id="element-19615" />
        </div>
        <button onClick={nextSort} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors shrink-0" data-id="element-19616">
          <ArrowUpDown className="w-3.5 h-3.5" data-id="element-19617" /> {sortLabels[sortType]}
        </button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-0.5" data-id="element-19618">
        {tabs.map(tab => <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${activeTab === tab.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-19619">
            {tab.label}{' '}
            <span className={`text-[10px] ${activeTab === tab.id ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-19620">
              {tab.count}
            </span>
          </button>)}
      </div>

      {/* List */}
      {currentList.length === 0 ? renderEmpty(activeTab === 'astrologers' ? t('userdash.favorites.noAstrologers') : activeTab === 'services' ? t('userdash.favorites.noServices') : activeTab === 'products' ? t('userdash.favorites.noProducts') : t('userdash.favorites.noCourses'), activeTab === 'astrologers' ? t('userdash.favorites.findAstrologer') : t('userdash.favorites.toCatalog'), activeTab === 'astrologers' ? 'astrologers' : 'services-marketplace') : <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-19621">
          {visibleItems.map((item: any) => {
        const isExpanded = expandedId === item.id;
        if (activeTab === 'astrologers') {
          const a = item as FavAstrologer;
          return <div key={a.id} data-id="element-19622">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(a.id)} data-id="element-19623">
                    <div className="relative shrink-0" data-id="element-19624">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${a.color}`} data-id="element-19625">
                        {a.initials}
                      </div>
                      {a.online && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-19626" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19627">
                      <div className="flex items-center gap-1.5" data-id="element-19628">
                        <span className="font-medium text-sm text-slate-900 truncate" data-id="element-19629">
                          {a.name}
                        </span>
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" data-id="element-19630" />
                        <span className="text-[10px] font-bold text-slate-700 shrink-0" data-id="element-19631">
                          {a.rating}
                        </span>
                        {!a.active && <Badge variant="warning" size="sm" className="text-[8px] py-0 h-3.5 px-1" data-id="element-19632">
                            {t('userdash.favorites.inactive')}
                          </Badge>}
                      </div>
                    </div>
                    <span className="text-xs font-medium text-indigo-600 shrink-0 hidden sm:block" data-id="element-19633">
                      {a.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('astrologers', a.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-19634">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-19635" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-19636" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-19637">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-19638">
                        <div className="text-[11px] text-slate-500" data-id="element-19639">
                          {a.spec}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-19640">
                          <span data-id="element-19641">{a.price}</span>
                          <span data-id="element-19642">
                            {a.reviews} {t('userdash.favorites.reviews')}
                          </span>
                          {a.nextSlot && <span className="flex items-center gap-0.5" data-id="element-19643">
                              <Calendar className="w-3 h-3" data-id="element-19644" /> {a.nextSlot}
                            </span>}
                          <span className="flex items-center gap-0.5" data-id="element-19645">
                            <Clock className="w-3 h-3" data-id="element-19646" />{' '}
                            {daysAgoLabel(a.addedDaysAgo)}
                          </span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-19647">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-19648">
                            {t('userdash.favorites.book')}
                          </Button>
                          <Button variant="outline" size="sm" className="text-xs h-7 px-3" onClick={() => showToast(t('userdash.favorites.chatOpened'))} data-id="element-19649">
                            <MessageSquare className="w-3 h-3 mr-1" data-id="element-19650" />{' '}
                            {t('userdash.favorites.write')}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('astrologer-profile')} data-id="element-19651">
                            <User className="w-3 h-3 mr-1" data-id="element-19652" />{' '}
                            {t('userdash.favorites.profile')}
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        if (activeTab === 'services') {
          const s = item as FavService;
          return <div key={s.id} data-id="element-19653">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(s.id)} data-id="element-19654">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.format === 'video' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-19655">
                      {s.format === 'video' ? <Video className="w-3.5 h-3.5" data-id="element-19656" /> : <FileText className="w-3.5 h-3.5" data-id="element-19657" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19658">
                      <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-19659">
                        {s.title}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-19660">
                      {s.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('services', s.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-19661">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-19662" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-19663" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-19664">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-19665">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-19666">
                          <span data-id="element-19667">{s.astrologer}</span>
                          <span className="flex items-center gap-0.5" data-id="element-19668">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-19669" />{' '}
                            {s.rating}
                          </span>
                          <span data-id="element-19670">
                            {s.format === 'video' ? t('userdash.favorites.video') : t('userdash.favorites.text')}
                          </span>
                          <span data-id="element-19671">{daysAgoLabel(s.addedDaysAgo)}</span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-19672">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('service')} data-id="element-19673">
                            {t('userdash.favorites.order')}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('service')} data-id="element-19674">
                            {t('userdash.favorites.details')}
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        if (activeTab === 'products') {
          const p = item as FavProduct;
          return <div key={p.id} data-id="element-19675">
                  <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(p.id)} data-id="element-19676">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0" data-id="element-19677">
                      {p.icon === 'book' ? <BookOpen className="w-3.5 h-3.5" data-id="element-19678" /> : <Headphones className="w-3.5 h-3.5" data-id="element-19679" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19680">
                      <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-19681">
                        {p.title}
                      </span>
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[8px] py-0 h-3.5 px-1 shrink-0 hidden sm:flex" data-id="element-19682">
                      {p.type}
                    </Badge>
                    <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-19683">
                      {p.price}
                    </span>
                    <button onClick={e => {
                e.stopPropagation();
                handleRemove('products', p.id);
              }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-19684">
                      <Heart className="w-3.5 h-3.5 fill-current" data-id="element-19685" />
                    </button>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-19686" />
                  </div>
                  {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-19687">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-19688">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-19689">
                          <span data-id="element-19690">
                            {t('userdash.favorites.type')}: {p.type}
                          </span>
                          <span data-id="element-19691">{daysAgoLabel(p.addedDaysAgo)}</span>
                        </div>
                        <div className="flex gap-2 pt-1" data-id="element-19692">
                          <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-19693">
                            {t('userdash.favorites.buy')}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-19694">
                            {t('userdash.favorites.details')}
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>;
        }
        // courses
        const c = item as FavCourse;
        return <div key={c.id} data-id="element-19695">
                <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50 transition-colors" onClick={() => toggle(c.id)} data-id="element-19696">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-19697">
                    <Play className="w-3.5 h-3.5" data-id="element-19698" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-19699">
                    <span className="font-medium text-sm text-slate-900 truncate block" data-id="element-19700">
                      {c.title}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-slate-900 shrink-0" data-id="element-19701">
                    {c.price}
                  </span>
                  <button onClick={e => {
              e.stopPropagation();
              handleRemove('courses', c.id);
            }} className="p-1 text-rose-400 hover:text-rose-600 shrink-0" data-id="element-19702">
                    <Heart className="w-3.5 h-3.5 fill-current" data-id="element-19703" />
                  </button>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-19704" />
                </div>
                {isExpanded && <div className="px-3 pb-3 -mt-0.5" data-id="element-19705">
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-id="element-19706">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500" data-id="element-19707">
                        <span data-id="element-19708">
                          {c.lessons} {t('userdash.favorites.lessons')} ·{' '}
                          {c.duration}
                        </span>
                        <span data-id="element-19709">{daysAgoLabel(c.addedDaysAgo)}</span>
                      </div>
                      <div className="flex gap-2 pt-1" data-id="element-19710">
                        <Button size="sm" className="text-xs h-7 px-3" onClick={() => onNavigate('product-detail')} data-id="element-19711">
                          {t('userdash.favorites.details')}
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>;
      })}
        </div>}

      {/* Load More */}
      {hasMore && <div className="text-center" data-id="element-19712">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => setVisibleCount(v => v + PAGE_SIZE)} data-id="element-19713">
            {t('userdash.favorites.showMore')} (
            {currentList.length - visibleCount} {t('userdash.favorites.left')})
          </Button>
        </div>}

      {/* Count info */}
      {currentList.length > 0 && <div className="text-center text-[10px] text-slate-400" data-id="element-19714">
          {t('userdash.favorites.showing')}{' '}
          {Math.min(visibleCount, currentList.length)}{' '}
          {t('userdash.favorites.of')} {currentList.length}
        </div>}
    </div>;
}